// Project Goal definitions — drives messaging, headlines, and framing
// across the app. Does NOT influence financial calculations.

export const PROJECT_GOALS = [
  {
    id: "flip",
    label: "Flip for profit",
    tagline: "Estimate renovation costs and calculate profit and ROI.",
    description:
      "Model purchase, renovation, and soft costs. Track profit, ROI, and margin to time a short-turnaround sale.",
    dashboardHeadline: "Project Profitability",
    dashboardSub: "How much this deal could return if you sell after renovating.",
    // Ordered list of metric ids to emphasise on the dashboard.
    emphasis: ["profit", "roi", "margin", "reno"],
    metrics: {
      profitLabel: "Est. Profit",
      profitSub: "Sale − Total Cost",
      roiLabel: "ROI",
      roiSub: "Profit ÷ Total Cost",
      marginLabel: "Margin",
      marginSub: "Profit ÷ Sale Price",
    },
    rooms: {
      heading: "Renovation Cost by Room",
      description: "See where the budget goes and trim rooms that don't move ROI.",
      perRoomShowPct: true,
    },
    exterior: {
      heading: "Essential upgrades vs cost control",
      note: "Avoid overspending on low-return exterior items.",
    },
    systems: {
      heading: "Essential upgrades vs cost control",
      note: "Prioritise systems that protect your ROI — skip low-impact upgrades.",
    },
  },
  {
    id: "sell",
    label: "Renovate to sell",
    tagline: "Understand renovation costs and maximise resale value.",
    description:
      "Plan targeted improvements that lift resale value. Focus on uplift and return on renovation rather than short-term flip velocity.",
    dashboardHeadline: "Resale Impact",
    dashboardSub: "Where your renovation spend translates into a stronger sale.",
    emphasis: ["reno", "valueAdded", "total", "margin"],
    metrics: {
      profitLabel: "Net Gain on Sale",
      profitSub: "Sale − Total Cost",
      roiLabel: "Return on Renovation",
      roiSub: "Net Gain ÷ Total Cost",
      marginLabel: "Sale Margin",
      marginSub: "Net Gain ÷ Sale Price",
    },
    rooms: {
      heading: "Renovation Cost by Room",
      description: "Focus on high-impact areas like kitchens and bathrooms.",
      perRoomShowPct: false,
    },
    exterior: {
      heading: "Curb appeal and resale impact",
      note: "Exterior improvements can significantly influence buyer perception.",
    },
    systems: {
      heading: "Peace-of-mind upgrades buyers notice",
      note: "Up-to-date systems reduce buyer hesitation and inspection pushback.",
    },
  },
  {
    id: "live",
    label: "Renovate to live",
    tagline: "Plan your renovation costs and spend wisely.",
    description:
      "Forecast what the renovation will actually cost you. Stay on budget and see how each decision impacts your total spend.",
    dashboardHeadline: "Renovation Budget Overview",
    dashboardSub: "A clear picture of what the renovation will cost and where the money goes.",
    emphasis: ["reno", "total", "costPerRoom", "costPerSqm"],
    metrics: {
      profitLabel: "Net Position",
      profitSub: "Hypothetical Sale − Total Cost",
      roiLabel: "Value vs Spend",
      roiSub: "Net Position ÷ Total Cost",
      marginLabel: "Headroom",
      marginSub: "Net Position ÷ Sale Price",
    },
    rooms: {
      heading: "Cost by Room",
      description: "Plan and manage your renovation budget by room.",
      perRoomShowPct: false,
    },
    exterior: {
      heading: "Comfort, functionality, and maintenance",
      note: "Focus on durability and long-term value.",
    },
    systems: {
      heading: "Comfort, functionality, and maintenance",
      note: "Choose systems that last — cheap now often costs more later.",
    },
  },
];

export const getGoal = (id) =>
  PROJECT_GOALS.find((g) => g.id === id) || PROJECT_GOALS[0];

/**
 * Simple estimated value added — how much the renovation is hoped to lift the
 * resale value by. Treated as a soft estimate for the "Renovate to sell" goal.
 * = target sale price − purchase price − total renovation cost
 *   ... capped at a non-negative placeholder when inputs are missing.
 */
export const estimatedValueAdded = (project, metrics) => {
  const sale = Number(project?.target_sale_price) || 0;
  const purchase = Number(project?.purchase_price) || 0;
  const reno = Number(metrics?.reno) || 0;
  if (sale <= 0 || purchase <= 0) return 0;
  return sale - purchase - reno;
};

/**
 * Render a short summary sentence for the dashboard insight card.
 * @param {object} project
 * @param {object} metrics
 * @param {(n:number)=>string} fmt  Currency formatter already bound to the project currency.
 * @returns {string}
 */
export const buildGoalInsight = (project, metrics, fmt) => {
  const goalId = project?.project_goal || "flip";
  const reno = fmt(metrics?.reno || 0);
  const total = fmt(metrics?.total || 0);
  const profit = fmt(metrics?.profit || 0);
  // metrics.roi is already a percentage (e.g. 13.66), so format it directly —
  // do NOT multiply by 100.
  const roi = `${(metrics?.roi || 0).toFixed(1)}%`;
  const valueAdded = fmt(estimatedValueAdded(project, metrics));

  if (goalId === "flip") {
    // For a flip, ROI is calculated against TOTAL project cost (purchase +
    // reno + soft). The headline number should match — saying "cost X" then
    // computing ROI against a different X is misleading.
    return `This deal totals ${total} (purchase + ${reno} reno + soft) and is projected to deliver an estimated profit of ${profit}, an ROI of ${roi} on total invested.`;
  }
  if (goalId === "sell") {
    return `This renovation is estimated to cost ${reno}, with an expected uplift in resale value of ${valueAdded}. Focus on key areas to maximise resale potential.`;
  }
  return `This renovation is estimated to cost ${reno}. Use this breakdown to plan and manage your budget effectively.`;
};
